import {
  Text as RNText,
  type TextProps as RNTextProps,
  StyleSheet,
} from "react-native";

import { appFontFamily } from "@/theme/fonts";
import type { AppTheme } from "@/theme/theme";
import { lineHeightFor } from "@/theme/tokens";
import { useTheme } from "@/theme/useTheme";

type Variant = "display" | "heading" | "body";
type Weight = "light" | "regular" | "medium" | "semiBold" | "bold";
type Tone = "default" | "muted" | "primary";
type Size = keyof AppTheme["fontSize"];

export type TextProps = RNTextProps & {
  /**
   * display = page titles in the Live Colour Tiles voice: light, lowercase, 32 px, tight leading;
   * heading = bold by default and the larger default size; body = regular. One system family.
   */
  variant?: Variant;
  weight?: Weight;
  size?: Size;
  tone?: Tone;
};

/** Display defaults to light, headings to bold, body text to regular. */
function fontWeight(
  theme: AppTheme,
  variant: Variant,
  weight: Weight | undefined,
): AppTheme["font"]["weight"][Weight] {
  return theme.font.weight[
    weight ??
      (variant === "display"
        ? "light"
        : variant === "heading"
          ? "bold"
          : "regular")
  ];
}

/** Text with the token type scale, weights and colours (FR-020, FR-021). */
export function Text({
  variant = "body",
  weight,
  size,
  tone = "default",
  style,
  ...rest
}: TextProps) {
  const theme = useTheme();
  const display = variant === "display";
  const fontSize =
    theme.fontSize[
      size ?? (display ? "x5l" : variant === "heading" ? "x2l" : "base")
    ];
  const color =
    tone === "muted"
      ? theme.color.textMuted
      : tone === "primary"
        ? theme.color.primaryText
        : theme.color.text;
  return (
    <RNText
      {...rest}
      style={StyleSheet.flatten([
        {
          fontFamily: appFontFamily(),
          fontWeight: fontWeight(theme, variant, weight),
          // Numbers line up in columns: "1 of 3", class numbers, prices.
          fontVariant: ["tabular-nums"],
          fontSize,
          lineHeight: lineHeightFor(fontSize, display ? 1.15 : undefined),
          color,
        },
        display && { textTransform: "lowercase" as const },
        style,
      ])}
    />
  );
}
